import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
    Compass, 
    Database, 
    Workflow, 
    Gem, 
    SlidersHorizontal,
    Bell,
    LogOut,
    Menu,
    X,
    Target,
    UserCircle,
    ChevronDown,
    ShieldCheck,
    Users,
    Activity,
    Star,
    FileCheck,
    LineChart,
    PieChart,
    History
} from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../services/AuthContext';
import { useCountry } from '../context/CountryContext';
import { usePartnerType } from '../hooks/usePartnerType';

interface BrokerLayoutProps {
    children: ReactNode;
}

const BrokerLayout = ({ children }: BrokerLayoutProps) => {
    const { t, i18n } = useTranslation();
    const { user, logout } = useAuth();
    const { activeCountry } = useCountry();
    const { isCompanyOwner } = usePartnerType();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isNotificationsMenuOpen, setIsNotificationsMenuOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, [location.pathname]);

    const getPath = (path: string) => {
        const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
        const fullPath = `${prefix}${path}`;
        return fullPath.startsWith('/') ? fullPath : `/${fullPath}`;
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        i18n.changeLanguage(newLang);
    };

    const isERealtyMode = import.meta.env.VITE_APP_MODE === 'erealty';

    const navigation = useMemo(() => {
        if (isERealtyMode) {
            return [
                { name: t('nav_home'), href: getPath('/partners/dashboard'), icon: Compass },
                { name: t('nav_inventory_hub'), href: getPath('/partners/inventory/e-realty'), icon: Database },
                { name: t('nav_co_pilot'), href: getPath('/partners/co-pilot'), icon: Gem },
            ];
        }

        if (isCompanyOwner) {
            return [
                { name: t('nav_home'), href: getPath('/partners/dashboard'), icon: Compass },
                { 
                    name: t('nav_insights'), 
                    href: '#', 
                    icon: LineChart,
                    children: [
                        { name: t('nav_grow_with_partners'), href: getPath('/partners/insights/grow'), icon: Activity },
                        { name: t('nav_top_projects'), href: getPath('/partners/insights/top'), icon: Star },
                    ]
                },
                { name: t('nav_sales'), href: getPath('/partners/sales/corporate'), icon: History },
                { name: t('nav_commissions'), href: getPath('/partners/commissions/team'), icon: PieChart },
                { name: t('nav_pipeline'), href: getPath('/partners/pipeline'), icon: Workflow },
                { name: t('nav_cils'), href: getPath('/partners/cils'), icon: FileCheck },
                { name: t('nav_team'), href: getPath('/partners/team'), icon: Users },
            ];
        }

        return [
            { name: t('nav_home'), href: getPath('/partners/dashboard'), icon: Compass },
            { name: t('nav_pipeline'), href: getPath('/partners/pipeline'), icon: Workflow },
            { name: t('nav_cils'), href: getPath('/partners/cils'), icon: FileCheck },
            { name: t('nav_sales'), href: getPath('/partners/sales'), icon: Target },
            { name: t('nav_commissions'), href: getPath('/partners/commissions'), icon: Gem },
            { name: t('nav_inventory'), href: getPath('/partners/inventory'), icon: Database },
        ];
    }, [activeCountry, isERealtyMode, isCompanyOwner, t]);

    useEffect(() => {
        // Automatically expand menus if a child is active
        const activeMenu = navigation.find(item => 
            item.children?.some(child => location.pathname === child.href)
        );
        if (activeMenu && !expandedMenus.includes(activeMenu.name)) {
            setExpandedMenus(prev => [...prev, activeMenu.name]);
        }
    }, [location.pathname]);

    const toggleMenu = (name: string) => {
        setExpandedMenus(prev => 
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
    };

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const renderNavItem = (item: any) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedMenus.includes(item.name);
        const isActive = location.pathname.startsWith(item.href);

        if (hasChildren) {
            return (
                <div key={item.name} className="space-y-1 relative group/menu">
                    <button
                        onClick={() => !isSidebarCollapsed && toggleMenu(item.name)}
                        className={`
                            flex items-center group relative
                            ${isSidebarCollapsed 
                                ? `w-10 h-10 justify-center rounded-md mb-1 transition-none ${isActive ? 'bg-white/10 text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'}` 
                                : `w-full justify-between py-3 px-4 rounded-md transition-all duration-200 ${isActive ? 'bg-navy-600/50 text-white' : 'text-[#94A3B8] hover:bg-navy-600/50 hover:text-white'}`
                            }
                        `}
                    >
                        {isSidebarCollapsed && (
                            <div className="absolute start-[calc(100%+8px)] px-3 py-2 bg-navy text-white text-sm font-bold rounded-[4px] opacity-0 group-hover:opacity-100 transition-all duration-100 pointer-events-none z-[100] whitespace-nowrap shadow-2xl border border-white/5 flex items-center min-w-[80px]">
                                {item.name}
                            </div>
                        )}
                        <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
                            <item.icon className={`w-5 h-5 shrink-0 transition-colors duration-200 ${isActive ? 'text-emerald-500' : 'opacity-80'}`} strokeWidth={1.5} />
                            {!isSidebarCollapsed && <span className={`text-sm font-medium whitespace-nowrap overflow-hidden ${isActive ? 'text-white' : ''}`}>{item.name}</span>}
                        </div>
                        {!isSidebarCollapsed && (
                            <ChevronDown 
                                size={16} 
                                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                            />
                        )}
                    </button>
                    
                    {isExpanded && !isSidebarCollapsed && (
                        <div className="ms-9 space-y-1">
                            {item.children.map((child: any) => (
                                child.external ? (
                                    <a
                                        key={child.name}
                                        href={child.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 px-4 py-2 rounded-md transition-all duration-200 text-sm font-medium text-[#94A3B8] hover:text-white"
                                    >
                                        {child.icon && <child.icon size={14} className="opacity-60" />}
                                        {child.name}
                                        <span className="ms-auto text-[9px] opacity-40">↗</span>
                                    </a>
                                ) : (
                                    <NavLink
                                        key={child.name}
                                        to={child.href}
                                        className={({ isActive }) => `
                                            flex items-center gap-3 px-4 py-2 rounded-md transition-all duration-200 text-sm font-medium
                                            ${isActive ? 'text-emerald-400 bg-emerald-500/10' : 'text-[#94A3B8] hover:text-white'}
                                        `}
                                    >
                                        {child.icon && <child.icon size={14} className={`transition-colors ${isActive ? 'text-emerald-400 opacity-100' : 'opacity-60'}`} />}
                                        {child.name}
                                    </NavLink>
                                )
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) => `
                    flex items-center group relative
                    ${isSidebarCollapsed 
                        ? `w-10 h-10 justify-center rounded-md mb-1 transition-none ${isActive ? 'bg-white/10 text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'}` 
                        : `w-full gap-3 py-3 px-4 rounded-md transition-all duration-200 ${isActive ? 'bg-navy-600/80 text-white shadow-sm ring-1 ring-emerald-500/30' : 'text-[#94A3B8] hover:bg-navy-600/50 hover:text-white'}`
                    }
                `}
            >
                {isSidebarCollapsed && (
                    <div className="absolute start-[calc(100%+8px)] px-3 py-2 bg-navy text-white text-sm font-bold rounded-[4px] opacity-0 group-hover:opacity-100 transition-all duration-100 pointer-events-none z-[100] whitespace-nowrap shadow-2xl border border-white/5 flex items-center min-w-[80px]">
                        {item.name}
                    </div>
                )}
                <item.icon className="w-5 h-5 shrink-0 opacity-80" strokeWidth={1.5} />
                {!isSidebarCollapsed && <span className="text-sm font-medium whitespace-nowrap overflow-hidden">{item.name}</span>}
            </NavLink>
        );
    };

    return (
        <div className="fixed inset-0 bg-navy dark:bg-black flex overflow-hidden overscroll-none">
            {/* --- Desktop Sidebar --- */}
            <aside className={`${isSidebarCollapsed ? 'w-[60px] overflow-visible' : 'w-[220px] overflow-hidden'} transition-[width] duration-300 flex-shrink-0 h-full bg-navy dark:bg-black text-white hidden lg:flex flex-col z-50`}>
                <div className={`${isSidebarCollapsed ? 'p-3 flex flex-col items-center' : 'p-6'}`}>
                    <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center mb-6' : 'gap-3 px-2 mb-10'} cursor-pointer overflow-hidden min-h-[24px]`} onClick={() => navigate(getPath('/partners/dashboard'))}>
                        {isSidebarCollapsed ? (
                            <img 
                                src="/laqyicon.svg" 
                                alt="LAQY" 
                                className="h-6 w-auto flex-shrink-0" 
                            />
                        ) : (
                            <img 
                                src="/laqy-partners-logo.svg" 
                                alt="LAQY Partners" 
                                className="h-8 w-auto flex-shrink-0 ms-1" 
                            />
                        )}
                    </div>
                    
                    <nav className={`space-y-1 no-scrollbar max-h-[calc(100vh-250px)] overscroll-contain ${isSidebarCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
                        {navigation.map((item) => renderNavItem(item))}
                    </nav>
                </div>

                <div className={`mt-auto border-t border-white/5 space-y-2 ${isSidebarCollapsed ? 'p-2' : 'p-4'}`}>
                    {!isSidebarCollapsed ? (
                        <>
                            <div className="flex items-center gap-3 px-2 py-2">
                                <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center flex-shrink-0 font-bold text-xs ">
                                     {user?.first_name?.[0] || user?.email?.[0] || 'B'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold truncate">{user?.first_name || t('nav_pro_broker')}</p>
                                    <p className="text-[10px] text-[#94A3B8]  tracking-normal">{t('nav_verified_account')}</p>
                                </div>
                            </div>
                            
                            <button 
                                onClick={logout}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-[#94A3B8] hover:text-white hover:bg-white/5 transition-all text-sm font-medium whitespace-nowrap"
                            >
                                <LogOut size={16} className="flex-shrink-0" />
                                {t('nav_logout')}
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col items-center gap-4 py-2">
                            <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center font-bold text-xs  shadow-sm">
                                {user?.first_name?.[0] || user?.email?.[0] || 'B'}
                            </div>
                            <button 
                                onClick={logout}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-[#94A3B8] hover:text-white hover:bg-white/5 transition-all relative group"
                            >
                                <div className="absolute start-[calc(100%+8px)] px-3 py-2 bg-navy text-white text-sm font-bold rounded-[4px] opacity-0 group-hover:opacity-100 transition-all duration-100 pointer-events-none z-[100] whitespace-nowrap shadow-2xl border border-white/5 flex items-center min-w-[80px]">
                                    {t('nav_logout')}
                                </div>
                                <LogOut size={16} />
                            </button>
                        </div>
                    )}
                    
                    <div className="flex transition-all px-4 py-2">
                        <button 
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className={`w-8 h-8 rounded-md bg-black/20 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer ${isSidebarCollapsed ? 'mx-auto' : 'ms-auto'}`}
                        >
                            <ChevronDown size={14} className={`transition-transform duration-300 ${isSidebarCollapsed ? '-rotate-90' : 'rotate-90'}`} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* --- Main Content Area --- */}
            <main className="flex-1 flex flex-col h-full min-w-0 bg-navy dark:bg-black relative">
                {/* Top Header */}
                <header className="h-[48px] flex-shrink-0 bg-transparent px-6 lg:px-8 flex items-center justify-between lg:justify-end z-40">
                    <div className="flex items-center gap-4 lg:hidden">
                        <button 
                            onClick={toggleMobileMenu}
                            className="p-2 -ms-2 text-white/80 hover:text-white"
                        >
                            <Menu size={24} />
                        </button>
                        <img 
                            src="/laqysymbolandlogo_premium.svg" 
                            alt="LAQY" 
                            className="h-5 w-auto brightness-0 invert" 
                        />
                    </div>

                    <div className="flex items-center gap-4 text-white relative">
                        {/* Notifications Dropdown */}
                        <div className="relative">
                            <button 
                                onClick={() => {
                                    setIsNotificationsMenuOpen(!isNotificationsMenuOpen);
                                    setIsProfileMenuOpen(false);
                                }}
                                className="relative w-8 h-8 flex items-center justify-center text-white/80 rounded-full transition-none"
                            >
                                <Bell size={18} strokeWidth={1.5} />
                                <span className="absolute top-1.5 end-1.5 w-2 h-2 bg-terracotta-500 rounded-full border-2 border-navy pt-0.5"></span>
                            </button>

                            {isNotificationsMenuOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setIsNotificationsMenuOpen(false)}></div>
                                    <div className="absolute end-0 top-full mt-2 w-80 bg-white rounded-md shadow-xl border border-slate-200 dark:border-slate-800 dark:bg-[#111] z-50 overflow-hidden text-slate-800 dark:text-white">
                                        <div className="p-4 border-b border-slate-200 dark:border-white/10">
                                            <h3 className="font-semibold text-base text-slate-700 dark:text-slate-200">{t('notifications_title')}</h3>
                                        </div>
                                        <div className="p-10 flex items-center justify-center text-slate-400 dark:text-slate-500 text-sm">
                                            {t('notifications_empty')}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-3 ps-2">
                            <button 
                                onClick={toggleLanguage}
                                className="text-sm font-bold text-white/80 transition-none uppercase"
                            >
                                {i18n.language === 'en' ? 'عربي' : 'English'}
                            </button>
                            
                            {/* Profile Dropdown */}
                            <div className="relative">
                                <div 
                                    onClick={() => {
                                        setIsProfileMenuOpen(!isProfileMenuOpen);
                                        setIsNotificationsMenuOpen(false);
                                    }}
                                    className="h-8 w-8 rounded-full bg-white/10 transition-none flex items-center justify-center overflow-hidden border border-white/10 cursor-pointer"
                                >
                                    <UserCircle size={20} className="text-white/80" />
                                </div>

                                {isProfileMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsProfileMenuOpen(false)}></div>
                                        <div className="absolute end-0 top-full mt-2 w-72 bg-white rounded-md shadow-2xl border border-slate-200 dark:border-slate-800 dark:bg-[#111] z-50 overflow-hidden text-slate-800 dark:text-white">
                                            <div className="p-5 border-b border-slate-200 dark:border-white/10 flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-full bg-[#1E6B9B] text-white flex items-center justify-center text-xl font-bold  shrink-0">
                                                    {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="font-semibold text-base text-slate-800 dark:text-slate-200 truncate">
                                                        {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : t('nav_pro_broker')}
                                                    </h3>
                                                    <p className="text-sm text-slate-400 dark:text-slate-500 truncate mt-0.5">
                                                        {user?.email}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="p-3">
                                                <button 
                                                    onClick={() => {
                                                        setIsProfileMenuOpen(false);
                                                        navigate(getPath('/partners/settings'));
                                                    }}
                                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 rounded-md transition-colors"
                                                >
                                                    <SlidersHorizontal size={18} className="text-slate-500" strokeWidth={1.5} />
                                                    {t('nav_settings')}
                                                </button>
                                                <button 
                                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 rounded-md transition-colors"
                                                >
                                                    <ShieldCheck size={18} className="text-slate-500" strokeWidth={1.5} />
                                                    {t('nav_terms_and_conditions')}
                                                </button>
                                            </div>

                                            <div className="p-3 border-t border-slate-200 dark:border-white/10">
                                                <button 
                                                    onClick={() => {
                                                        setIsProfileMenuOpen(false);
                                                        logout();
                                                    }}
                                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 rounded-md transition-colors"
                                                >
                                                    <LogOut size={18} className="text-slate-500" strokeWidth={1.5} />
                                                    {t('nav_logout')}
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content Container - The Carded Design */}
                <div className="flex-1 bg-white dark:bg-[#0A0A0A] rounded-t-lg shadow-2xl shadow-black/10 relative flex flex-col overflow-hidden mt-1 ms-1">
                    <div 
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain"
                    >
                        <div className="p-6 md:p-8 lg:p-10 w-full max-w-[1600px] mx-auto min-h-full">
                            {children}
                        </div>
                    </div>
                </div>
            </main>

            {/* --- Mobile Drawer --- */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={toggleMobileMenu}></div>
                    <aside className="absolute inset-y-0 start-0 w-72 bg-navy text-white flex flex-col p-6 transition-transform">
                        <div className="flex items-center justify-between mb-10 px-2">
                             <div className="flex items-center gap-2" onClick={() => { navigate(getPath('/partners/dashboard')); toggleMobileMenu(); }}>
                                <img src="/laqysymbolandlogo_premium.svg" alt="LAQY" className="h-5 brightness-0 invert" />
                             </div>
                            <button onClick={toggleMobileMenu} className="p-2 -me-2 text-white/70">
                                <X size={24} />
                            </button>
                        </div>

                        <nav className="space-y-1 overflow-y-auto no-scrollbar">
                            {navigation.map((item) => renderNavItem(item))}
                        </nav>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default BrokerLayout;
